<template>
  <WdsStack gap-sm direction-column>
    <!-- Multi-select keeps the list open between picks; picks live in the rows below, never in the input. -->
    <WdsSelect
      :model-value="noSelection"
      multi-select
      searchable
      custom-search
      :list="unassigned"
      :placeholder="searchPlaceholder"
      :disabled="disabled"
      data-test="owner-search"
      @update:model-value="add($event.at(-1) ?? null)"
      @update:search="$emit('search', $event)"
      @load-more="$emit('loadMore')"
    >
      <template #option="{ option }">
        <WdsStack gap-sm align-center :data-test="`owner-candidate-${option.value}`">
          <AvatarWithFallback sm class="tw-shrink-0" :avatar="option.avatar" :id="option.id" />
          <WdsStack direction-column class="tw-min-w-0">
            <WdsText :text="option.label" class="tw-truncate" />
            <WdsText v-if="option.email" secondary :text="option.email" class="tw-truncate" />
          </WdsStack>
        </WdsStack>
      </template>
    </WdsSelect>

    <div v-if="!model.length" data-test="owners-empty">
      <slot name="empty">
        <WdsText
          secondary
          class="tw-py-3"
          :text="$t('No one is assigned yet. Search your team to add someone.')"
        />
      </slot>
    </div>

    <div v-else class="tw-flex tw-flex-col">
      <WdsStack
        v-for="owner in model"
        :key="owner.id"
        gap-md
        align-center
        class="tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-py-3"
        :data-test="`owner-row-${owner.id}`"
      >
        <AvatarWithFallback sm class="tw-shrink-0" :avatar="owner.avatar" :id="owner.id" />

        <WdsStack direction-column class="tw-min-w-0 tw-flex-1">
          <WdsStack gap-sm align-center>
            <WdsText :text="owner.label" class="tw-truncate" />
            <WdsTag
              v-if="!savedIds.includes(owner.id)"
              success
              :data-test="`owner-added-${owner.id}`"
            >
              {{ $t('Added') }}
            </WdsTag>
          </WdsStack>
          <WdsText v-if="owner.email" secondary :text="owner.email" class="tw-truncate" />
        </WdsStack>

        <WdsSelect
          v-if="responsibilities.length"
          :model-value="responsibilityOf(owner)"
          can-deselect
          class="tw-w-[190px] tw-shrink-0"
          :list="responsibilities"
          :placeholder="$t('No responsibility')"
          :disabled="disabled"
          :data-test="`owner-responsibility-${owner.id}`"
          @update:model-value="setResponsibility(owner, $event)"
        />

        <WdsButtonIcon
          sm
          transparent-background
          :disabled="disabled"
          :aria-label="$t('Unassign :name', { name: owner.label })"
          :data-test="`remove-owner-${owner.id}`"
          @click="remove(owner)"
        >
          <WdsIcon :name="WdsIconNameEnum.CLOSE" :size="16" />
        </WdsButtonIcon>
      </WdsStack>
    </div>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsButtonIcon,
  WdsIcon,
  WdsIconNameEnum,
  WdsSelect,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { $t } from '@/composables/translation'
import AvatarWithFallback from '@/components/AvatarWithFallback.vue'

export type OwnerOption = {
  id: string
  value: string
  label: string
  email?: string | null
  avatar?: string | null
}
export type ResponsibilityOption = { value: string; label: string }
export type OwnerSelection = OwnerOption & { responsibility: string | null }

const {
  candidates = [],
  responsibilities = [],
  disabled = false,
  savedIds = [],
  defaultResponsibility = '',
  searchPlaceholder = $t('Search your team to add someone'),
} = defineProps<{
  candidates?: OwnerOption[]
  responsibilities?: ResponsibilityOption[]
  disabled?: boolean
  /** Owners already saved on the record; anyone else on the list is tagged as added. */
  savedIds?: string[]
  /** Given to the first owner when none is chosen. */
  defaultResponsibility?: string
  searchPlaceholder?: string
}>()

defineEmits<{ search: [string]; loadMore: [] }>()

const model = defineModel<OwnerSelection[]>({ default: () => [] })

const noSelection: OwnerOption[] = []

/** Someone already on the list cannot be added a second time. */
const unassigned = computed(() =>
  candidates.filter((candidate) => !model.value.some((owner) => owner.id === candidate.id)),
)

const add = (person: OwnerOption | null) => {
  if (!person) {
    return
  }

  // A record with one owner has no other candidate for the default responsibility.
  const responsibility = model.value.length ? null : defaultResponsibility || null

  model.value = [...model.value, { ...person, responsibility }]
}

const responsibilityOf = (owner: OwnerSelection) =>
  responsibilities.find((option) => option.value === owner.responsibility) ?? null

const setResponsibility = (owner: OwnerSelection, selection: ResponsibilityOption | null) => {
  model.value = model.value.map((existing) =>
    existing.id === owner.id ? { ...existing, responsibility: selection?.value ?? null } : existing,
  )
}

const remove = (owner: OwnerSelection) => {
  model.value = model.value.filter((existing) => existing.id !== owner.id)
}
</script>
